import styled from 'styled-components';
import { LauncherWindowData, useLauncherWindowStore } from '../../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { DropdownInput } from '../../common/baseInputs/DropdownInput';
import { IconSelectInput } from '../../common/baseInputs/IconSelectInput';
import { NumberInput } from '../../common/baseInputs/NumberInput';
import { useScene } from '../../../context/sceneContext';

export const TextPropsPanel = () => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const selectedElementID = UIData.selectedElementIDs.slice(-1)[0];

  const data = scene.getSelectedElementsData(
    {
      fontFamily: undefined,
      fontWeight: undefined,
      fontSize: undefined,
      lineHeight: undefined,
      letterSpacing: undefined,
      whiteSpace: undefined,
      textAlign: undefined,
      alignItems: undefined,
      textOverflow: undefined,
    },
    'props.class'
  );

  const updateValue = (key: string, value?: string | number) =>
    scene.updateSelectedElementsData({ [key]: value }, 'props.class', 'textInput');

  const computedStyle = scene.getElementComputedStyle(selectedElementID);

  return (
    <Container>
      <Row style={{ padding: '4px 5px' }}>
        {/* <Row style={{ width: '60px' }}>
          <Dropdown
            trigger={
              <DropdownButton>
                <i className="icon-triple-dots" />
              </DropdownButton>
            }
            style={{
              width: '16px',
              height: '30px',
            }}
            horizontalAlign="left"
            closeOnClick={false}
          >
            <DropdownContentContainer>

            </DropdownContentContainer>
          </Dropdown>
        </Row> */}
        <Row>
          <IconSelectInput
            value={data.whiteSpace}
            defaultValue="wrap"
            data={[
              {
                value: 'wrap',
                iconClassName: 'icon-text-wrap',
                id: 'text-wrap',
                tooltip: 'Wrap Text',
              },
              {
                value: 'nowrap',
                iconClassName: 'icon-text-no-wrap',
                id: 'text-no-wrap',
                tooltip: "Don't Wrap Text",
              },
            ]}
            onChange={(newValue) =>
              scene.updateSelectedElementsData(
                {
                  whiteSpace: newValue,
                  textOverflow: undefined,
                  overflow: undefined,
                },
                'props.class',
                'textInput'
              )
            }
          />
        </Row>
        {data.whiteSpace === 'nowrap' ? (
          <Row>
            <IconSelectInput
              value={data.textOverflow}
              data={[
                {
                  value: 'ellipsis',
                  iconClassName: 'icon-text-truncate',
                  id: 'text-truncate',
                  tooltip: 'Text Truncate',
                },
              ]}
              onChange={(newValue) =>
                scene.updateSelectedElementsData(
                  {
                    display: 'inline-block',
                    justifyContent: undefined,
                    alignItems: undefined,
                    textOverflow: newValue,
                    overflow: newValue ? 'hidden' : undefined,
                  },
                  'props.class',
                  'textInput'
                )
              }
            />
          </Row>
        ) : (
          <Row>
            <IconSelectInput
              value={data.textAlign}
              defaultValue="left"
              data={[
                {
                  value: 'left',
                  iconClassName: 'icon-text-align-left',
                  id: 'text-align-left',
                  tooltip: 'Text Align Left',
                },
                {
                  value: 'center',
                  iconClassName: 'icon-text-align-center',
                  id: 'text-align-center',
                  tooltip: 'Text Align Center',
                },
                {
                  value: 'right',
                  iconClassName: 'icon-text-align-right',
                  id: 'text-align-right',
                  tooltip: 'Text Align Right',
                },
                {
                  value: 'justify',
                  iconClassName: 'icon-text-align-justify',
                  id: 'text-align-justify',
                  tooltip: 'Text Align Justify',
                },
              ]}
              onChange={(newValue) => updateValue('textAlign', newValue)}
            />
          </Row>
        )}
      </Row>

      <Row>
        <DropdownInput
          iconClassName="icon-text-font-family"
          value={data.fontFamily}
          placeholder={computedStyle?.fontFamily}
          onChange={(newValue) => updateValue('fontFamily', newValue === undefined ? undefined : String(newValue))}
          options={[...launcherWindowData.fonts.map((font) => ({ value: font.fontFamily, label: font.fontFamily }))]}
          id="input-font-family"
          tooltip="Font Family"
        />
      </Row>
      <Row>
        <Row>
          <NumberInput
            iconClassName="icon-text-size"
            value={data.fontSize}
            onChange={(newValue) => updateValue('fontSize', newValue)}
            placeholder={computedStyle?.fontSize}
            precision={0}
            minValue={4}
            defaultUnit="px"
            id="input-text-size"
            tooltip="Font Size"
          />
        </Row>
        <Row>
          <DropdownInput
            iconClassName="icon-text-weight"
            value={data.fontWeight}
            placeholder={computedStyle?.fontWeight}
            onChange={(newValue) => updateValue('fontWeight', Number(newValue))}
            options={[
              { value: 100, label: '100 - Thin', shortLabel: '100' },
              { value: 200, label: '200 - Light', shortLabel: '200' },
              { value: 300, label: '300 - Book', shortLabel: '300' },
              { value: 400, label: '400 - Regular', shortLabel: '400' },
              { value: 500, label: '500 - Medium', shortLabel: '500' },
              { value: 600, label: '600 - Semi-Bold', shortLabel: '600' },
              { value: 700, label: '700 - Bold', shortLabel: '700' },
              { value: 800, label: '800 - Extra-Bold', shortLabel: '800' },
              { value: 900, label: '900 - Heavy', shortLabel: '900' },
            ]}
            horizontalAlign="right"
            id="input-font-weight"
            tooltip="Font Weight"
          />
        </Row>
      </Row>
      <Row>
        <Row>
          <NumberInput
            iconClassName="icon-text-line-height"
            value={data.lineHeight}
            onChange={(newValue) => updateValue('lineHeight', newValue)}
            placeholder={computedStyle?.lineHeight}
            defaultUnit="px"
            minValue={0}
            id="input-line-height"
            tooltip="Line Height"
          />
        </Row>
        <Row>
          <NumberInput
            iconClassName="icon-text-letter-spacing"
            value={data.letterSpacing}
            onChange={(newValue) => updateValue('letterSpacing', newValue)}
            placeholder={computedStyle?.letterSpacing}
            defaultUnit="px"
            minValue={0}
            id="input-letter-spacing"
            tooltip="Letter Spacing"
          />
        </Row>
      </Row>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  gap: 5px;
`;

const DropdownButton = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  border-radius: 5px;

  & > i {
    rotate: 90deg;
    font-size: 16px;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  transition:
    background-color 0.2s,
    color 0.2s;
  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }
`;
